import type { MediaType } from './media-unit-variants.js';

/**
 * The nine formatka-sourced client price fields an offer's merged table
 * (`offer-unified-table` capability) lets an editor override per unit,
 * mirroring `computePriceBreakdown`/`PRICE_LINE_ITEMS`
 * (`packages/screens/src/media-units/detail/lib/price-breakdown.ts`) — the same
 * fields the media-unit card already breaks down, minus `clientPriceNet`
 * itself (already the offer's own editable headline price).
 *
 * Single source of truth shared by the api (write validation, totals) and
 * the web (which columns to render as editable). See
 * `OFFER_PRICE_SUM_FIELD_IDS` for the subset that feeds `subtotalNet`.
 */
export const OFFER_PRICE_OVERRIDE_FIELD_IDS = [
  'catalogPrice',
  'discountedPrice',
  'lastMinutePrice',
  'starterPrice',
  'clientProduction',
  'clientInstallation',
  'clientDismantling',
  'clientAdditionalFees',
  'muralEcoProduction',
] as const;
export type OfferPriceOverrideFieldId = (typeof OFFER_PRICE_OVERRIDE_FIELD_IDS)[number];

/**
 * DOOH units carry no production/installation/dismantling site work and no
 * mural-specific eco-production line, so the offer's price editor offers a
 * narrower set for them than for every other `MediaTypeEnum` value.
 */
const DOOH_PRICE_OVERRIDE_FIELD_IDS: readonly OfferPriceOverrideFieldId[] = [
  'catalogPrice',
  'discountedPrice',
  'starterPrice',
  'clientProduction',
];

/**
 * The four core line items that sum into the offer's `subtotalNet`, mirroring
 * `PRICE_LINE_ITEMS`'s non-optional entries in `price-breakdown.ts`.
 * `clientAdditionalFees` is deliberately excluded — it is reported as a
 * separate "additional services" figure outside `subtotalNet`, mirroring
 * that same module's `isOptional: true` line item. `catalogPrice`/
 * `discountedPrice`/`lastMinutePrice`/`starterPrice` are also excluded —
 * they stay editable reference values and never sum into anything.
 */
export const OFFER_PRICE_SUM_FIELD_IDS: readonly OfferPriceOverrideFieldId[] = [
  'clientProduction',
  'muralEcoProduction',
  'clientInstallation',
  'clientDismantling',
];

/**
 * Which of the nine `OFFER_PRICE_OVERRIDE_FIELD_IDS` the offer's price editor
 * offers for a unit of the given media type. `dooh` gets the narrower
 * four-field set; every other `MediaTypeEnum` value gets the full nine.
 */
export function getOfferPriceOverrideFieldIds(
  mediaType: MediaType | string | null,
): readonly OfferPriceOverrideFieldId[] {
  return mediaType === 'dooh' ? DOOH_PRICE_OVERRIDE_FIELD_IDS : OFFER_PRICE_OVERRIDE_FIELD_IDS;
}
